import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { action, computed, makeObservable, observable } from 'mobx';
import { RateInput, RatesOverride } from 'types/productServiceV2';
import { OverrideRatesProduct } from './types';

export class ProductRatesForm {
  protected static buildFormController(Rates: RateInput[]): FormController<DefaultFormFields> {
    const fields = Rates.reduce((acc, rate) => {
      acc[rate.key] = new FormControl<string>(Number(rate.value).toFixed(2), {
        validations: ['validRate'],
      });
      return acc;
    }, {} as DefaultFormFields);
    return new FormController<DefaultFormFields>(fields);
  }

  private _productName: string;

  protected _formController: FormController<DefaultFormFields>;
  protected _initialRates: RateInput[];
  protected _ratesDomainData: Omit<RatesOverride, 'rates'>;

  get productName(): string {
    return this._productName;
  }

  get formController(): FormController<DefaultFormFields> {
    return this._formController;
  }

  get ratesOverrideData(): RatesOverride {
    return {
      portfolioId: this._ratesDomainData.portfolioId,
      productId: this._ratesDomainData.productId,
      bcIndex: this._ratesDomainData.bcIndex,
      rates: this._initialRates.map((rate) => ({
        ...rate,
        value: Number(this._formController.fields[rate.key].value),
      })),
    };
  }

  public applyDiscount(discountPercentage: number): void {
    Object.keys(this._formController.fields).forEach((key) => {
      this._formController.fields[key].value = (
        Number(this._formController.fields[key].value) *
        (1 - discountPercentage / 100)
      ).toFixed(2);
    });
  }

  public resetToInitialRates(): void {
    Object.keys(this._formController.fields).forEach((key) => {
      this._formController.fields[key].value = this._initialRates.find((rate) => rate.key === key)?.value || '';
    });
  }

  constructor(product: OverrideRatesProduct) {
    this._formController = ProductRatesForm.buildFormController(product.rates);
    this._initialRates = product.rates;
    this._productName = product.productName;
    this._ratesDomainData = {
      portfolioId: product.portfolioId,
      productId: product.productId,
      bcIndex: product.bcIndex,
    };

    makeObservable<ProductRatesForm, '_formController' | '_initialRates' | '_productName' | '_ratesDomainData'>(this, {
      _formController: observable,
      _initialRates: observable,
      _productName: observable,
      _ratesDomainData: observable,
      productName: computed,
      formController: computed,
      ratesOverrideData: computed,
      applyDiscount: action.bound,
      resetToInitialRates: action.bound,
    });
  }
}
